import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Stack } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { AutoCompleteSelectUI, FormLabelUI } from '../Interface';
import folderTemplateService from '../../service/folderTemplate.service';
import { generateAutocompleteItems } from '../../utils/helper';

const createFolderTree = (folders) => {
  const rootFolders = [];
  const folderMap = new Map(folders.map((folder) => [String(folder.id), { ...folder, children: [] }]));

  folderMap.forEach((folder) => {
    const parent = folder.parentId == null ? null : folderMap.get(String(folder.parentId));
    (parent?.children ?? rootFolders).push(folder);
  });

  return rootFolders;
};

const FolderPreview = ({ folder, level = 0 }) => (
  <>
    <Stack
      direction="row"
      justifyContent="space-between"
      p={1}
      pl={1 + level * 2}
      mb={0.5}
      borderRadius={1}
      border={1}
      borderColor="divider"
    >
      <Stack direction="row" spacing={1}>
        <FolderIcon fontSize="small" sx={{ color: folder.color }} />
        <Typography variant="body2">{folder.name}</Typography>
      </Stack>
    </Stack>
    {folder.children.map((child) => (
      <FolderPreview key={child.id} folder={child} level={level + 1} />
    ))}
  </>
);

const FolderTemplateImportDialog = ({ open, handleClose, handleImportFolder }) => {
  const [templateData, setTemplateData] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedTemplate(null);
      setTemplateData([]);
      setTemplateOptions([]);
      return;
    }

    getTemplates();
  }, [open]);

  const getTemplates = () => {
    folderTemplateService.getTemplates().then((data) => {
      setTemplateData(data);
      setTemplateOptions(generateAutocompleteItems(data, 'name', 'id'));
    });
  };

  const folderTemplateItems = templateData.find((item) => item.id === selectedTemplate)?.folderTemplateItem ?? [];
  const folderTree = useMemo(() => createFolderTree(folderTemplateItems), [folderTemplateItems]);

  const handleCreate = () => {
    handleImportFolder(folderTemplateItems);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Sablon importálás</DialogTitle>
      <DialogContent>
        <Box mt={1}>
          <AutoCompleteSelectUI
            variant="outlined"
            size="small"
            label={<FormLabelUI text="Sablonok" />}
            dataset={templateOptions}
            onChange={(_e, newVal) => setSelectedTemplate(newVal?.value ?? null)}
            selectedValue={selectedTemplate}
          />

          {folderTemplateItems.length > 0 && (
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Létrehozandó mappák
              </Typography>

              {folderTree.map((folder) => (
                <FolderPreview key={folder.id} folder={folder} />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Mégse</Button>
        <Button onClick={handleCreate} variant="contained" disabled={!folderTemplateItems.length}>
          Létrehozás
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderTemplateImportDialog;
